import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconDownload, IconMoneybag } from "@tabler/icons-react";
import { bFileCategoriesEnum, errorNotification, tu } from "beitary-shared";
import { BBox, MoveBackButton } from "components";
import { useCServices } from "hooks/useCService/useCService";
import { useSubmitState } from "hooks/useSubmitState";
import { useUploadFile } from "hooks/useUploadFile";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { mu } from "utils/money_utils";
import { utils, write, writeFile } from "xlsx";

export interface AccountsReceivableReportProps {}

export const AccountsReceivableReport = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [submitState, setSubmitState] = useSubmitState();

  const [data, setData] = useState<any>([]); // TODO fix

  const [{ submitFile, submitFileState }] = useUploadFile({
    filesCategory: bFileCategoriesEnum.Values.REPORT,
  });

  const totalOutstandingBalance = useMemo(
    () =>
      data.reduce(
        (acc: number, item: any) => acc + item.outstanding_balance,
        0
      ),
    [data]
  );
  const totalCreditBalance = useMemo(
    () => data.reduce((acc: number, item: any) => acc + item.credit_balance, 0),
    [data]
  );
  const totalAccountsReceivable = useMemo(
    () =>
      data.reduce(
        (acc: number, item: any) => acc + item.accounts_receivable,
        0
      ),
    [data]
  );

  const query = useCServices().reports.accountsReceivableReport;

  const runQuery = async () => {
    setSubmitState("pending-response");
    const result = await query();

    if (result.type === "success") {
      if (result.payload) setData(result.payload.data);
      setSubmitState("success");
    } else {
      showNotification(
        errorNotification({
          message: t("ERROR_GENERATING_REPORT"),
        })
      );
      setSubmitState("error");
      return;
    }
  };

  const downloadXslx = async () => {
    const summarySheet = utils.json_to_sheet([
      {
        date: tu.getDateAndTimeString(tu.getCurrentDateTime()),
        count: data.length,
      },
    ]);
    const dataSheet = utils.json_to_sheet(data);

    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, summarySheet, t("SUMMARY"));
    utils.book_append_sheet(workBook, dataSheet, t("ACCOUNTS_RECEIVABLE"));

    const buf = write(workBook, { bookType: "xlsx", type: "buffer" });

    const title = `${t("ACCOUNTS_RECEIVABLE_REPORT")} ${tu.getYYYYMMDDString(
      tu.getCurrentDateTime()
    )}.xlsx`;

    const file = new File([buf], title, { type: "application/octet-stream" });

    await submitFile(file);

    writeFile(workBook, title);
  };

  return (
    <Container fluid>
      <Stack spacing="xl">
        <Group mt="xl">
          <MoveBackButton />
        </Group>

        <BBox
          header={<Text weight={500}>{t("ACCOUNTS_RECEIVABLE_REPORT")}</Text>}
        >
          <Stack p="xl">
            <Group position="apart" align="flex-end">
              <Group></Group>
              <Group>
                <Button
                  onClick={runQuery}
                  loading={submitState === "pending-response"}
                >
                  {t("GENERATE_REPORT")}
                </Button>
                <Button
                  variant="outline"
                  loading={submitFileState === "pending-response"}
                  leftIcon={<IconDownload size={18} />}
                  disabled={submitState !== "success"}
                  onClick={downloadXslx}
                >
                  {t("DOWNLOAD")}
                </Button>
              </Group>
            </Group>

            <Box mt="md" mb="md">
              {submitState === "success" && (
                <Stack>
                  <Group grow mt="xl" align="flex-start">
                    <Paper withBorder p="md" radius="md">
                      <Group spacing="xs" mb="xl">
                        <Text color="dimmed" weight="bold">
                          {t("TOTAL_OUTSTANDING_BALANCE")}
                        </Text>
                        <IconMoneybag stroke={2} color={theme.colors.red[5]} />
                      </Group>
                      <Group spacing="xs">
                        <Text size="xl" weight="bold">
                          {mu.toD(totalOutstandingBalance)}
                        </Text>
                      </Group>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                      <Group spacing="xs" mb="xl">
                        <Text color="dimmed" weight="bold">
                          {t("TOTAL_CREDIT_BALANCE")}
                        </Text>
                        <IconMoneybag
                          stroke={2}
                          color={theme.colors.green[5]}
                        />
                      </Group>
                      <Group spacing="xs">
                        <Text size="xl" weight="bold">
                          {mu.toD(totalCreditBalance)}
                        </Text>
                      </Group>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                      <Group spacing="xs" mb="xl">
                        <Text color="dimmed" weight="bold">
                          {t("TOTAL_ACCOUNTS_RECEIVABLE")}
                        </Text>
                        <IconMoneybag stroke={2} color={theme.colors.blue[5]} />
                      </Group>
                      <Group spacing="xs">
                        <Text size="xl" weight="bold">
                          {mu.toD(totalAccountsReceivable)}
                        </Text>
                      </Group>
                    </Paper>
                  </Group>
                  <Text mt="xl">
                    {data.length} {t("ITEMS_FOUND")}
                  </Text>
                </Stack>
              )}
            </Box>
          </Stack>
        </BBox>
      </Stack>
    </Container>
  );
};
